import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

interface ScrollFadeWrapperProps {
  children: React.ReactNode;
  className?: string;
  fadeWidth?: number;
  fadeColor?: string;
}

const ScrollFadeWrapper = ({
  children,
  className,
  fadeWidth = 32,
  fadeColor = "rgba(253,253,253,1)",
}: ScrollFadeWrapperProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el) return;

    checkScroll();

    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    if (content) {
      observer.observe(content);
    }

    return () => observer.disconnect();
  }, [checkScroll]);

  const fadeColorTransparent = fadeColor.replace(/,\s*1\)$/, ",0)");

  return (
    <div className={cn("relative", className)}>
      {canScrollLeft && (
        <div
          className="pointer-events-none absolute left-0 top-0 bottom-0 z-10"
          style={{
            width: fadeWidth,
            background: `linear-gradient(to right, ${fadeColor} 0%, ${fadeColorTransparent} 100%)`,
          }}
        />
      )}
      {canScrollRight && (
        <div
          className="pointer-events-none absolute right-0 top-0 bottom-0 z-10"
          style={{
            width: fadeWidth,
            background: `linear-gradient(to left, ${fadeColor} 0%, ${fadeColorTransparent} 100%)`,
          }}
        />
      )}
      <div
        ref={scrollRef}
        className="w-full overflow-x-auto"
        onScroll={checkScroll}
      >
        <div ref={contentRef} className="w-fit min-w-full">
          {children}
        </div>
      </div>
    </div>
  );
};

export default ScrollFadeWrapper;
